import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const createBrandStoryVideoSkill: Skill = {
    name: SKILL_NAMES.createBrandStoryVideo,
    description:
        'Visionary script writing over full-bleed cinematic media, an inspirational voiceover, and an epic, awe-inspiring soundtrack. Great for branding videos or story videos.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Brand Story',
        preview: 'brand-story',
        frames: [
            'brand-story-1',
            'brand-story-2',
            'brand-story-3',
            'brand-story-4',
            'brand-story-5',
        ],
    },
    instructions: `# Create Brand Story Video
For a grandiose, visionary, cinematic brand story video: a script followed
word-for-word (or composed on the user's behalf in bold, sweeping,
inspirational language), broken into short fragment scenes of roughly 3-5
seconds each, most of them a big simple line of text over full-bleed
cinematic media, voiced by an inspirational AI voiceover over an epic,
awe-inspiring soundtrack.

## Setup
Use \`${SKILL_NAMES.gatherRequirements}\` to get necessary info, skipping anything already provided.
Use that skill's general suggestions, but also specifically make sure we get:
- Script/source material/topic
- Voice (search according to below instructions to offer good options)
- Template
- Any specific brand media they'd like to include

Show users options of cinematic & inspirational templates. Search if none of the available ones seem an obvious fit.

## Narration and music
- **Voice: inspirational.** Search with \`${TOOL_NAMES.searchVoices}\` for a tone that
  reads as warm, emotive, and inspiring — pass
  "inspirational"/"emotive"/"warm" through the \`search\` free-text field
  alongside whatever \`tone\` values the tool recognizes.
- **Music: awe-inspiring and epic — and it must actually end up in the
  video.** Search \`${TOOL_NAMES.searchMusic}\` for a soundtrack with real scale: cinematic,
  sweeping, orchestral-feeling tracks that build toward something, not
  gentle background ambience. Search terms include "epic"/"awe-inspiring"/"cinematic
  build"/"orchestral". The video's music should
  feel like a trailer or an anthem, not a soft underscore. Apply the chosen
  track to the video (don't stop at searching) and set its volume so it
  supports the voiceover without competing with it.
- Well-placed pauses give the narration room to breathe — a beat after the
  opening line, ahead of a turn in the story. Sparingly, not between every
  sentence.

## Script
- **If the user gave a specific script, follow it word-for-word.** Don't
  paraphrase, trim, or otherwise rewrite their wording — the only thing this
  recipe changes about it is how it's broken into scenes and which of its
  words get displayed on screen (see the text rule below).
- **If no script was given, compose one** from the source they pointed to (a
  fetched page, an uploaded document) or from research on their topic if
  that's all they gave. Load and follow \`${SKILL_NAMES.writeVoicedrivenVideoScript}\`.
  **Tone should be grandiose, visionary, and inspirational — this is the defining trait of
  this format, not a light garnish on top of
  an otherwise ordinary explainer script.** Reach for the scale of the idea:
  what does this mean for the world, the future, the people it touches? Use
  bold, sweeping language and big, declarative statements rather than
  measured, matter-of-fact description. Compare the register you're aiming
  for against a plain corporate summary of the same facts:
  - Flat and generic (avoid): "Our company was founded in 2010 and has grown
    to serve customers in 40 countries."
  - Grandiose and visionary (aim for this): "It started as one idea. Today,
    it reaches every corner of the world." / "Forty countries. One mission."
  Every fact still has to be true and grounded in the source material — this
  is elevated language, not invented claims. But default to the version of
  each sentence that swings for scale, emotion, and vision, not the version
  that just states the fact. Short, declarative, weighty sentences read
  better here than long qualified ones. Write it budgeted to the duration
  gathered in Setup.
- **This grandiose register applies even when following the user's own
  script word-for-word** — the wording itself doesn't change in that case
  (see the rule above), but everything else in this recipe (the media, the
  pacing, the music) should still be built to match that visionary, epic
  feel regardless of who wrote the words.
- **Break the script into sentence fragments, each its own scene.** Where a
  full sentence would run long, split it at natural clause boundaries into
  fragments delivered back to back — each fragment is a complete scene beat,
  generally lasting **3-5 seconds**.
- **Build the narrative spine first** — the throughline the script is
  making — before exact wording, so one changed beat doesn't force a full
  rewrite (unless the user's own script is being followed verbatim, in which
  case the spine is whatever they wrote).

## The text rule — show only the core words, not the full sentence
This is the one rule in this recipe that inverts the usual "on-screen text
matches everything spoken" approach used elsewhere:
- **Each scene's on-screen text shows only the core, important words of what
  is spoken during it — filler and connecting words are spoken but
  deliberately not displayed.** If the spoken fragment is "We bring together
  4,500 strategy consultants," the on-screen text is "4,500 strategy
  consultants" — not the full sentence, and not a paraphrase either. Every
  word that IS shown must be a word that was actually spoken, taken verbatim
  from the fragment, just with the connective tissue (subject, verb, "the,"
  "a," "that," and similar) left off screen.
- **Don't over-trim past the point of clarity.** The words left on screen
  must still stand alone and make sense to a viewer who can't hear the audio
  — a name, a number, a specific claim, a key phrase. If trimming down to
  just the "core" leaves something ambiguous or meaningless out of context,
  keep enough of the surrounding words to restore that clarity.
- This is a distinct, deliberate choice for this format specifically — it is
  not the same rule the rest of this recipe set uses, and it doesn't apply
  anywhere else. Text amount here is a genuine edit-down of the spoken words,
  not a verbatim transcript of them.

## Scene design variety — full-bleed media leads, everything else is seasoning
Unlike a general "wide variety" instruction, this format draws from a
specific set of design types, but they are not weighted equally:
- **Text on full-bleed, dimmed cinematic media is the default and should be
  the majority of scenes.** A big, simple line of core text over a
  full-frame, dimmed cinematic shot is the signature look of this format —
  when in doubt, this is the design to reach for. Most of the video should
  be built from these.
- **Text on a colored background**, **animated captions**
  (ANIMATED_CAPTIONS category), and **split-screen designs** are secondary —
  real, deliberate choices, but used more sparingly, as rhythm breaks against
  the full-bleed-media majority rather than equal rotation partners. Reach
  for split-screen specifically when a scene is making two points at once (a
  comparison, a before/after, two things discussed side by side); reach for
  a colored background or animated captions occasionally, for a beat that
  benefits from a harder visual break from the media-driven look.
Still avoid repeating the exact same design on two consecutive scenes, and
still vary things enough that the video doesn't feel monotonous — but the
imbalance is intentional: this should read as a full-bleed cinematic media
piece with occasional accents, not four design types getting equal airtime.

## Asset timing — text is trimmed, timing isn't
- **Anchor each scene's timing (\`${TOOL_NAMES.addVoiceoverScene}\` with \`words\`) to the
  complete spoken fragment for that scene** — the full run of words as
  actually spoken, filler included. The scene's timing (and therefore the
  scene's on-screen duration) is set by how long the whole fragment takes to
  say, even though the text asset displayed during that time shows only the
  distilled core words from the text rule above. Timing tracks the full
  spoken fragment; the visible text is the edited-down version of it.
- **Target roughly 3-5 seconds of spoken duration per scene.** If a fragment
  runs meaningfully longer than that, split it into two fragments/scenes at
  the next natural clause boundary rather than letting one scene run long or
  cramming two ideas' worth of core text into one design.
- Never split a fragment mid-phrase, and never trim a scene's \`words\` span
  short of what's actually spoken in it — the audio for the full fragment
  must play out during that scene regardless of how few of those words end up
  on screen.
- When one full sentence had to be split across multiple fragments/scenes,
  keep the design and media direction consistent across them so they read as
  one continuous moment rather than unrelated cuts.

## Media
- **Every scene has its own relevant, high-quality, cinematic media** — not
  a generic stock clip loosely on-topic, but a shot that's obviously
  connected to the specific words in that scene, in a genuinely cinematic
  style (real depth, motion, and production quality, not a flat stock photo).
- **Keep a consistent media direction across the whole video** — a coherent
  visual mood (tone, pacing of motion, color feel) rather than clips that
  look like they came from different sources or styles. Search with that
  consistency in mind rather than picking whatever's individually the best
  match scene by scene.
- If the user provided specific brand media, use it for the scene(s) it
  belongs to. Otherwise load \`${SKILL_NAMES.findStockMedia}\` and search for cinematic,
  high-production footage; load \`${SKILL_NAMES.findDamMedia}\` too if a DAM is connected
  and the shot is brand-specific. Don't fall back to a mismatched or awkward
  clip just to fill a scene — a text-on-colored-background or animated-
  captions design (no media required) is a better choice than a poor visual
  fit.
- Reframe within the asset so the subject is well-composed and nothing
  important sits under text (\`${SKILL_NAMES.cropMedia}\` skill).

## Styling
- Colors and styling come from the template or the user's brand kit — apply a
  kit when one exists or the user references their brand (\`${SKILL_NAMES.applyBrandKit}\`
  skill); don't hand-pick color overrides unprompted.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard pass. On top of that, confirm:
- **Turn captions off** (\`${TOOL_NAMES.setCaptions}\` with \`enabled: false\`) unless the
  user specifically asked for them on.
- **If the user supplied their own script, the spoken narration matches it
  word-for-word** — nothing paraphrased, trimmed, or added.
- **Spot-check several scenes' on-screen text against their full spoken
  fragment.** Every displayed word should be a word that was actually
  spoken (no invented or paraphrased text), and the display should be the
  distilled core of that fragment, not the full sentence — but not so
  over-trimmed that it reads as unclear on its own.
- **Check every scene's actual duration** (\`${TOOL_NAMES.getVideoState}\`) — each should
  land at roughly 3-5 seconds. A scene running notably longer should have its
  fragment split; a scene that's oddly short for what's spoken should be
  checked for incorrect timing.
- **Full-bleed dimmed media with simple text is genuinely the majority
  design across the video**, with colored-background, animated-caption, and
  split-screen scenes appearing only occasionally as accents — if the mix
  feels close to even across all four, that's a defect; pull it back toward
  full-bleed media. Still no design repeats back-to-back.
- Every piece of media obviously fits its scene's words and reads as
  genuinely cinematic; the video overall feels like one consistent visual
  direction rather than a mix of styles.
- **If the script was composed by this recipe (not supplied by the user),
  read it back and confirm it's genuinely grandiose and visionary** — bold,
  sweeping, declarative language, not a flat recitation of facts dressed up
  with a couple of inspirational words. If any line reads like a plain
  corporate summary, rewrite it.
- **Confirm a music track is actually applied** (\`${TOOL_NAMES.getVideoState}\`), and
  that it's genuinely epic and awe-inspiring — a track with real scale and
  build, not gentle background ambience — at a volume that supports rather
  than competes with the voiceover.
- The voice reads as inspirational and warm.
- Captions are off unless the user asked for them on.`,
};
